import { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Chip, Divider,
    TextField, Stack
} from "@mui/material";
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RemoveIcon from '@mui/icons-material/Remove';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TarefaPrioridade, TarefaType } from "../../types/TarefaType";
import { api } from "../../services/api";
import { AlertInfo } from "../../components/Alerts/Alert";
import moment from "moment";

type Props = {
    open: boolean;
    tarefas: TarefaType[];
    onClose: () => void;
    onUpdated: () => void;
};

type RemarcarForm = { motivo: string; };

// ─── Helpers ───────────────────────────────────────────────────────────────────
const PRIORIDADE_ORDEM: Record<TarefaPrioridade, number> = { urgente: 1, prioridade: 2, normal: 3 };

function isVencida(d: any) { return moment(d).isBefore(moment(), 'day'); }
function isHoje(d: any) { return moment(d).isSame(moment(), 'day'); }
function isAmanha(d: any) { return moment(d).isSame(moment().add(1, 'day'), 'day'); }

function labelData(dateStr: string) {
    if (isVencida(dateStr)) return `Vencida — ${moment(dateStr).format('DD/MM/YYYY')}`;
    if (isHoje(dateStr)) return `Hoje — ${moment(dateStr).format('DD/MM/YYYY')}`;
    if (isAmanha(dateStr)) return `Amanhã — ${moment(dateStr).format('DD/MM/YYYY')}`;
    return moment(dateStr).format('dddd, DD/MM/YYYY');
}
function corData(dateStr: string) {
    if (isVencida(dateStr)) return '#c62828';
    if (isHoje(dateStr)) return '#1565c0';
    if (isAmanha(dateStr)) return '#2e7d32';
    return '#555';
}
function bgData(dateStr: string) {
    if (isVencida(dateStr)) return '#ffebee';
    if (isHoje(dateStr)) return '#e3f2fd';
    if (isAmanha(dateStr)) return '#e8f5e9';
    return '#f5f5f5';
}

function groupByDate(tarefas: TarefaType[]): [string, TarefaType[]][] {
    const map = new Map<string, TarefaType[]>();
    tarefas.forEach(t => {
        const key = String(t.data_tarefa).slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(t);
    });
    const entries: [string, TarefaType[]][] = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    entries.forEach(([, list]) =>
        list.sort((a: TarefaType, b: TarefaType) => PRIORIDADE_ORDEM[a.prioridade_tarefa] - PRIORIDADE_ORDEM[b.prioridade_tarefa])
    );
    return entries;
}

// ─── StatusBadge compacto ──────────────────────────────────────────────────────
function StatusBadge({ tarefa, vencida, hoje }: { tarefa: TarefaType; vencida: boolean; hoje: boolean }) {
    if (tarefa.status_tarefa === 'aguardando_edicao')
        return <Chip size="small" icon={<PendingActionsIcon />} label="Aguard. Edição"
            sx={{ ml: 1, flexShrink: 0, bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 'bold', '& .MuiChip-icon': { color: '#4527a0' } }} />;
    if (tarefa.status_tarefa === 'reagendamento_solicitado')
        return <Chip size="small" label="Reagend. Solicitado" color="warning" sx={{ ml: 1, flexShrink: 0 }} />;
    if (vencida) return <Chip size="small" label="Vencida" color="error" sx={{ ml: 1, flexShrink: 0 }} />;
    if (hoje) return <Chip size="small" label="Hoje" color="primary" sx={{ ml: 1, flexShrink: 0 }} />;
    return <Chip size="small" label={moment(tarefa.data_tarefa).format('DD/MM/YYYY')} color="default" sx={{ ml: 1, flexShrink: 0 }} />;
}

// ─── PriorityChip compacto ─────────────────────────────────────────────────────
function PriorityChip({ prioridade }: { prioridade: TarefaPrioridade }) {
    const base = { fontSize: '0.68rem', height: 20 };
    if (prioridade === 'urgente')
        return <Chip size="small" icon={<ErrorOutlineIcon />} label="Urgente"
            sx={{ ...base, bgcolor: '#ffebee', color: '#c62828', fontWeight: 'bold', '& .MuiChip-icon': { color: '#c62828', fontSize: 13 } }} />;
    if (prioridade === 'prioridade')
        return <Chip size="small" icon={<PriorityHighIcon />} label="Prioridade"
            sx={{ ...base, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold', '& .MuiChip-icon': { color: '#e65100', fontSize: 13 } }} />;
    return <Chip size="small" icon={<RemoveIcon />} label="Normal"
        sx={{ ...base, bgcolor: '#f5f5f5', color: '#757575', '& .MuiChip-icon': { color: '#9e9e9e', fontSize: 13 } }} />;
}

// ─── DateGroupHeader compacto ──────────────────────────────────────────────────
function DateGroupHeader({ dateStr }: { dateStr: string }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.8}
            sx={{ px: 2, py: 1, bgcolor: bgData(dateStr), borderBottom: `1px solid ${corData(dateStr)}22` }}>
            <CalendarTodayIcon sx={{ fontSize: 13, color: corData(dateStr) }} />
            <Typography variant="caption" fontWeight="bold"
                sx={{ color: corData(dateStr), textTransform: 'capitalize', letterSpacing: 0.3 }}>
                {labelData(dateStr)}
            </Typography>
        </Stack>
    );
}

export default function TarefasPendentesDialog({ open, tarefas, onClose, onUpdated }: Props) {
    const [remarcandoId, setRemarcandoId] = useState<number | null>(null);
    const [remarcarForm, setRemarcarForm] = useState<RemarcarForm>({ motivo: '' });
    const [loading, setLoading] = useState(false);

    async function handleConcluir(id_tarefa: number) {
        setLoading(true);
        try {
            const response = await api.post(`/updatetarefa/${id_tarefa}`, { status_tarefa: 'concluida' });
            if (response.data.success) { AlertInfo({ icon: 'success', title: 'Tarefa concluída!' }); onUpdated(); }
            else AlertInfo({ icon: 'error', title: response.data.message || 'Falha ao atualizar' });
        } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
        setLoading(false);
    }

    async function handleRemarcar(id_tarefa: number) {
        if (!remarcarForm.motivo.trim())
            return AlertInfo({ icon: 'info', title: 'Informe o motivo da remarcação' });
        setLoading(true);
        try {
            const response = await api.post(`/updatetarefa/${id_tarefa}`, {
                status_tarefa: 'aguardando_edicao',
                observacao_reagendamento: remarcarForm.motivo,
            });
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: 'Remarcação solicitada!' });
                setRemarcandoId(null);
                setRemarcarForm({ motivo: '' });
                onUpdated();
            } else AlertInfo({ icon: 'error', title: response.data.message || 'Falha ao solicitar' });
        } catch { AlertInfo({ icon: 'error', title: 'Falha na conexão' }); }
        setLoading(false);
    }

    const grupos = groupByDate(tarefas);
    const totalUrgentes = tarefas.filter(t => t.prioridade_tarefa === 'urgente').length;
    const totalHoje = tarefas.filter(t => isHoje(t.data_tarefa)).length;
    const totalVencidas = tarefas.filter(t => isVencida(t.data_tarefa)).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff', pb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AssignmentIcon />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">Suas Tarefas Pendentes</Typography>
                        <Typography variant="caption">
                            {totalUrgentes > 0 && `🔴 ${totalUrgentes} urgente(s)  `}
                            {totalHoje > 0 && `📅 ${totalHoje} para hoje  `}
                            {totalVencidas > 0 && `⚠️ ${totalVencidas} vencida(s)`}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {grupos.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">Nenhuma tarefa pendente.</Typography>
                    </Box>
                ) : (
                    grupos.map(([dateStr, tarefasDoGrupo], gi) => (
                        <Box key={dateStr}>
                            {gi > 0 && <Divider />}
                            <DateGroupHeader dateStr={dateStr} />

                            {tarefasDoGrupo.map((tarefa, index) => {
                                const urgente = tarefa.prioridade_tarefa === 'urgente';
                                const prio = tarefa.prioridade_tarefa === 'prioridade';
                                const remarcando = remarcandoId === tarefa.id_tarefa;
                                const aguardandoEdicao = tarefa.status_tarefa === 'aguardando_edicao';
                                const podeAgir = tarefa.status_tarefa === 'pendente';
                                const vencida = isVencida(tarefa.data_tarefa);
                                const hoje = isHoje(tarefa.data_tarefa);

                                return (
                                    <Box key={tarefa.id_tarefa}>
                                        {index > 0 && <Divider sx={{ mx: 2, borderStyle: 'dashed' }} />}
                                        <Box sx={{
                                            p: 2,
                                            borderLeft: aguardandoEdicao ? '4px solid #7e57c2'
                                                : urgente ? '4px solid #c62828'
                                                : prio ? '4px solid #e65100'
                                                : undefined,
                                            bgcolor: aguardandoEdicao ? '#faf5ff' : urgente ? '#fff9f9' : 'transparent',
                                        }}>
                                            {/* Header */}
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                                <Box flex={1}>
                                                    <Stack direction="row" spacing={0.8} alignItems="center" mb={0.4} flexWrap="wrap">
                                                        <PriorityChip prioridade={tarefa.prioridade_tarefa} />
                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                            {tarefa.titulo_tarefa}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                                <StatusBadge tarefa={tarefa} vencida={vencida} hoje={hoje} />
                                            </Stack>

                                            {tarefa.descricao_tarefa && (
                                                <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                    {tarefa.descricao_tarefa}
                                                </Typography>
                                            )}

                                            <Typography variant="caption" color="text.disabled">
                                                Atribuída por: <strong>{tarefa.nome_criador}</strong>
                                            </Typography>

                                            {/* Banner: aguardando edição */}
                                            {aguardandoEdicao && (
                                                <Box sx={{ mt: 1, p: 1, bgcolor: '#ede7f6', borderRadius: 1, border: '1px solid #ce93d8' }}>
                                                    <Typography variant="caption" color="#4527a0">
                                                        ✏️ Remarcação solicitada — aguardando edição
                                                    </Typography>
                                                    {tarefa.observacao_reagendamento && (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Motivo: {tarefa.observacao_reagendamento}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Form inline de remarcação */}
                                            {remarcando && (
                                                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                                                    <Typography variant="caption" fontWeight="bold" color="#4527a0" display="block" mb={1}>
                                                        Motivo da remarcação
                                                    </Typography>
                                                    <TextField fullWidth size="small" multiline rows={2}
                                                        placeholder="Descreva o motivo..."
                                                        sx={{ mb: 1, bgcolor: '#fff', borderRadius: 1 }}
                                                        value={remarcarForm.motivo}
                                                        onChange={e => setRemarcarForm({ motivo: e.target.value })} />
                                                    <Stack direction="row" spacing={1}>
                                                        <Button size="small" variant="contained" disableElevation
                                                            disabled={loading}
                                                            sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}
                                                            onClick={() => handleRemarcar(tarefa.id_tarefa)}>
                                                            Confirmar
                                                        </Button>
                                                        <Button size="small" variant="outlined" color="inherit"
                                                            onClick={() => { setRemarcandoId(null); setRemarcarForm({ motivo: '' }); }}>
                                                            Cancelar
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            )}

                                            {/* Ações: só exibe quando status é pendente */}
                                            {!remarcando && podeAgir && (
                                                <Stack direction="row" spacing={1} mt={1.5}>
                                                    <Button size="small" variant="contained" color="success" disableElevation
                                                        startIcon={<CheckCircleOutlineIcon />} disabled={loading}
                                                        onClick={() => handleConcluir(tarefa.id_tarefa)}>
                                                        Concluir
                                                    </Button>
                                                    <Button size="small" variant="outlined"
                                                        startIcon={<EventRepeatIcon />} disabled={loading}
                                                        sx={{ color: '#4527a0', borderColor: '#7e57c2', '&:hover': { borderColor: '#4527a0', bgcolor: '#f3e5f5' } }}
                                                        onClick={() => setRemarcandoId(tarefa.id_tarefa)}>
                                                        Remarcar
                                                    </Button>
                                                </Stack>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">Fechar</Button>
                <Button onClick={onClose} variant="contained" color="primary" disableElevation href="/tarefas">
                    Ver Todas as Tarefas
                </Button>
            </DialogActions>
        </Dialog>
    );
}
